import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useWs } from "@/context/WsContext";
import { Button } from "@/components/ui/button";
import {
  Sparkles, LayoutDashboard, Home, Briefcase, CalendarDays, CalendarCheck, Wallet, Store,
  ShieldCheck, Users, Scale, FileText, LogOut, Menu, X, ClipboardList, Bell, CheckCheck,
  UserCircle, PiggyBank, Settings,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ROLE_TITLES = { pm: "Property Manager", cleaner: "Cleaner Workspace", admin: "Admin Console" };

const NAV = {
  pm: [
    { to: "/pm", label: "Dashboard", icon: LayoutDashboard },
    { to: "/pm/properties", label: "Properties", icon: Home },
    { to: "/pm/jobs", label: "Job Pipeline", icon: Briefcase },
    { to: "/pm/calendar", label: "Calendar", icon: CalendarDays },
    { to: "/pm/payments", label: "Payments", icon: Wallet },
    { to: "/pm/profile", label: "Profile", icon: UserCircle },
  ],
  cleaner: [
    { to: "/cleaner", label: "Dashboard", icon: LayoutDashboard },
    { to: "/cleaner/marketplace", label: "Marketplace", icon: Store },
    { to: "/cleaner/jobs", label: "My Jobs", icon: ClipboardList },
    { to: "/cleaner/calendar", label: "Calendar", icon: CalendarDays },
    { to: "/cleaner/availability", label: "Availability", icon: CalendarCheck },
    { to: "/cleaner/earnings", label: "Earnings", icon: Wallet },
    { to: "/cleaner/onboarding", label: "Verification", icon: ShieldCheck },
    { to: "/cleaner/profile", label: "Profile", icon: UserCircle },
  ],
  admin: [
    { to: "/admin", label: "Overview", icon: LayoutDashboard },
    { to: "/admin/revenue", label: "Revenue", icon: PiggyBank },
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/jobs", label: "Jobs", icon: Briefcase },
    { to: "/admin/payouts", label: "Payouts", icon: Wallet },
    { to: "/admin/disputes", label: "Disputes", icon: Scale },
    { to: "/admin/background-checks", label: "Background Checks", icon: ShieldCheck },
    { to: "/admin/settings", label: "Settings", icon: Settings },
    { to: "/admin/logs", label: "System Logs", icon: FileText },
  ],
};

function NotificationBell() {
  const { notifications, unread, markRead, markAllRead } = useWs();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 rounded-md hover:bg-slate-100 transition-colors" data-testid="notification-bell">
          <Bell className="w-5 h-5 text-sapphire" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-signal text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
              {unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-white">
        <div className="flex items-center justify-between px-2 py-1">
          <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-ocean flex items-center gap-1" data-testid="mark-all-read">
              <CheckCheck className="w-3 h-3" /> Mark all read
            </button>
          )}
        </div>
        <DropdownMenuSeparator />
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 && (
            <div className="px-3 py-6 text-sm text-muted-foreground text-center">No notifications yet</div>
          )}
          {notifications.slice(0, 15).map((n) => (
            <DropdownMenuItem
              key={n.id}
              className={`flex flex-col items-start gap-0.5 cursor-pointer ${n.read ? "opacity-60" : ""}`}
              onClick={() => {
                markRead(n.id);
                if (n.job_id) navigate(`/jobs/${n.job_id}`);
              }}
              data-testid="notification-item"
            >
              <span className="text-sm font-medium">{n.message}</span>
              <span className="text-[11px] text-muted-foreground">{new Date(n.created_at).toLocaleString()}</span>
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const nav = NAV[user?.role] || [];

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 h-full w-64 bg-sapphire text-white flex flex-col transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        <div className="px-6 py-6 flex items-center gap-2 border-b border-white/10">
          <div className="w-9 h-9 bg-ocean flex items-center justify-center rounded-md">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold text-lg tracking-tight">Clean Slate</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Connect</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {nav.map((item) => {
            const active = location.pathname === item.to;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  active ? "bg-ocean text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="px-3 py-4 border-t border-white/10">
          <button
            onClick={() => { logout(); navigate("/login"); }}
            className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white w-full transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-white border-b border-border px-4 lg:px-8 h-16 flex items-center justify-between">
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="hidden lg:block">
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              {ROLE_TITLES[user?.role] || "Admin Console"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2" data-testid="user-menu">
                  <div className="w-9 h-9 rounded-md bg-ocean text-white flex items-center justify-center text-sm font-bold font-heading">
                    {initials}
                  </div>
                  <div className="hidden sm:block text-left leading-tight">
                    <div className="text-sm font-semibold">{user?.name}</div>
                    <div className="text-[11px] text-muted-foreground capitalize">{user?.role}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white w-48">
                <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 max-w-[1600px] w-full">{children}</main>
      </div>
    </div>
  );
}
